import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { filter } from 'rxjs';
import { SidenavContentService } from '../../shared/sidenav/sidenav-content/sidenav-content.service';
import { NotificationsSidenavComponent } from '../../notifications/notifications-sidenav/notifications-sidenav.component';
import { PropertyManagementCompanyDataService } from 'app/services/property-management-company/property-manager-company-data.service';
import { OrganizationDataService } from 'app/services/organization/organization-data.service';
import { GrowlerService } from 'app/services/growler.service';
import { NotificationsService } from 'app/services/notifications/notifications-service';
import { AvatarBlockModule } from '../../shared/avatar-block/avatar-block.module';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatListModule } from '@angular/material/list';
import { AnimatedDelayDirective, AnimatedDirective } from '../../shared/directives/animated.directive';
import { HostListenerService } from '../../../../services/host-listener.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { LaunchpadDemoModeDeactivatedDialogComponent } from '../../launchpad/launchpad-demo-mode-dialog/launchpad-demo-mode-deactivated-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { HeaderMenuSidenavComponent, HeaderMenuSidenavInput, HeaderMenuSidenavOutput } from '../header-menu-sidenav/header-menu-sidenav.component';
import { HeaderMenuItemsComponent } from '../header-menu-items/header-menu-items.component';
import { map } from 'rxjs/operators';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { avatarInitials } from '../../../../models/entities/base/avatar';

export interface HeaderMenuProfile
{
    id: number;
    name: string;
    initials: string;
    notifications: number;
}

@UntilDestroy()
@Component({
    selector: 'app-header-menu',
    templateUrl: './header-menu.component.html',
    styleUrls: ['./header-menu.component.scss'],
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatBadgeModule,
        AsyncPipe,
        MatMenuModule,
        MatDividerModule,
        NgIf,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule,
        AvatarBlockModule,
        MatAutocompleteModule,
        MatListModule,
        AnimatedDirective,
        AnimatedDelayDirective,
        MatSlideToggleModule,
        MatDialogModule,
        HeaderMenuItemsComponent,
    ],
})
export class HeaderMenuComponent implements OnInit
{
    profile: HeaderMenuProfile;
    loadingProfile = false;
    notificationsCount = 0;
    showNotifications = false;
    exitDemoMode = this._fb.control<boolean>(true);

    constructor(
            private _activatedRoute: ActivatedRoute,
            private _fb: FormBuilder,
            private _notificationService: NotificationsService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _organizationDataService: OrganizationDataService,
            private _sidenav: SidenavContentService,
            private _host: HostListenerService,
            private _router: Router,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId$() {
        return this._activatedRoute.paramMap.pipe(map((params) => +params?.get('pmcId')));
    }

    get pmc$() {
        return this._propertyManagementCompanyData.pmc$.pipe(filter((company) => !!company));
    }

    get showDemoModeToggle() {
        return this._organizationDataService.demoOrg$;
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    get tabletMode$() {
        return this._host.isTabletMode();
    }

    ngOnInit() {
        const jwt = LegFiJwtService.read();
        this.profile = {
            id: jwt.orgId,
            name: jwt.orgName,
            initials: avatarInitials(jwt.orgName),
            notifications: 0,
        };

        this.pmcId$.pipe(
                filter((pmcId) => !!pmcId),
                untilDestroyed(this)).subscribe({
            next: () => this.loadingProfile = true,
        });

        this.pmc$.pipe(untilDestroyed(this)).subscribe({
            next: (company) => {
                this.profile = {
                    id: -1,
                    name: company.name,
                    initials: company.initials,
                    notifications: this.notificationsCount ?? 0,
                };
                this.showNotifications = true;
                this.loadingProfile = false;
            }
        });

        this.exitDemoMode.valueChanges.pipe(
                filter((value) => !value),
                untilDestroyed(this),
        ).subscribe({
            next: () => {
                this.exitDemoMode.setValue(true); // force back to true
                this._dialog.open(LaunchpadDemoModeDeactivatedDialogComponent, {
                    panelClass: ['v2-dialog', 'info-layout'],
                    width: MatDialogSizes.MD,
                    autoFocus: 'dialog',
                    disableClose: true,
                }).afterClosed().subscribe({
                    next: () => this._router.navigate(['/pmc', this._propertyManagementCompanyData.pmcId, 'communities', 'add']),
                });
            },
        });

        this._notificationService.unreadCount$.pipe(untilDestroyed(this)).subscribe({
            next: (count) => this.notificationsCount = this.profile.notifications = count,
        });

        this._notificationService.getNotifications().pipe(untilDestroyed(this)).subscribe({
            error: () => this._growler.oops('There was a problem loading notifications.'),
        });
    }

    openNotifications() {
        this._sidenav.open(NotificationsSidenavComponent, {
            initialLoadingState: true,
            parentClass: 'notifications-layout',
        });
    }

    openMobileHeaderMenu() {
        const data: HeaderMenuSidenavInput = {
            profile: this.profile,
        };

        this._sidenav.open(HeaderMenuSidenavComponent, {
            data,
            parentClass: 'menu-layout',
        }).beforeClosed().subscribe({
            next: (res: HeaderMenuSidenavOutput) => {
                if (res?.switchToNotifications) {
                    this.openNotifications();
                }
            }
        });
    }
}
